import { httpClient } from '@/core/api/client/httpClient'
import { getAppEnv } from '@/core/config/env'
import { getStoredToken } from '@/core/auth/session/session.storage'

export interface ProductoResumen {
  id: string
  numeroContrato: string | null
  tipoProducto: string | null
  estadoProducto: string | null
  capital: number | null
  rendimiento: number | null
  totalCapitalRendimiento: number | null
  plazo: number | null
  tasaInteres: number | null
  fechaInicio: string | null
  fechaVencimiento: string | null
}

export interface CuotaCrecimiento {
  periodo: number | null
  fechaInicial: string | null
  fechaVencimiento: string | null
  capital: number | null
  rentaPeriodo: number | null
  capitalFinal: number | null
  capitalRenta: number | null
  rentaAcumulada: number | null
  bonoAnual: number | null
  tasa: number | null
  bonoAPagarAnual: number | null
  rentabilidad: number | null
  estadoCuota: number | null
  rendimientoIncremental: number | null
  rendimientoAcumuladoIncremental: number | null
  bonoAcumulado: number | null
  valorAcumuladoSinBono: number | null
  valorAcumulado: number | null
}

export interface ProductoDetalle extends ProductoResumen {
  periodicidad: number | null
  valorProyectadoLiquidar: number | null
  inversorNombre: string | null
  inversorCelular: string | null
  inversorEmail: string | null
  inversionInicial: number | null
  inversionMensual: number | null
  costoOperativo: number | null
  totalRentabilidad: number | null
  totalCostoOperativo: number | null
  totalRentaPeriodo: number | null
  totalAportesMensuales: number | null
  valorActualLiquidar: number | null
  bonoAnual: number | null
  totalSumaDeBonos: number | null
  cuotas: CuotaCrecimiento[]
}

export interface DocumentoInversion {
  id: string | null
  name: string | null
  fileName: string | null
  documentTypeName: string | null
  documentCode: string | null
  documentGroup: 'PROJECTION' | 'CONTRACT' | 'EXCLUDED' | string | null
  consolidationOrder: number | null
  isRequired: boolean | null
  downloadUrl: string | null
  createdOn: string | null
}

export interface DocumentosInversion {
  investmentId: string | null
  available: boolean
  count: number
  documents: DocumentoInversion[]
}

export interface DocumentConsolidationJob {
  jobId: string
  investmentId: string
  jobType: string
  languageCode: string
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | string
  resultFileName: string | null
  errorMessage: string | null
  attemptCount: number
}

interface BffListResponse {
  success: boolean
  data: { productos: ProductoResumen[] }
}

interface BffDetalleResponse {
  success: boolean
  data: ProductoDetalle
}

interface BffDocumentosResponse {
  success: boolean
  data: DocumentosInversion
}

interface BffCreateJobResponse {
  success: boolean
  data: { jobId: string; status: string }
}

interface BffJobResponse {
  success: boolean
  data: DocumentConsolidationJob
}

export const misProductosService = {
  async listar(lang?: string): Promise<ProductoResumen[]> {
    const normalizedLang = lang?.trim().toLowerCase() === 'en' ? 'en' : 'es'
    const res = await httpClient.get<BffListResponse>(`/api/mis-productos?lang=${normalizedLang}`)
    return res.data?.productos ?? []
  },

  async obtenerDetalle(id: string, lang?: string): Promise<ProductoDetalle | null> {
    const normalizedLang = lang?.trim().toLowerCase() === 'en' ? 'en' : 'es'
    const res = await httpClient.get<BffDetalleResponse>(`/api/mis-productos/${id}?lang=${normalizedLang}`)
    return res.data ?? null
  },

  async obtenerDocumentos(id: string, lang?: string): Promise<DocumentosInversion> {
    const normalizedLang = lang?.trim().toLowerCase() === 'en' ? 'en' : 'es'
    const res = await httpClient.get<BffDocumentosResponse>(
      `/api/mis-productos/${id}/documentos?lang=${normalizedLang}`,
    )
    return res.data ?? { investmentId: id, available: false, count: 0, documents: [] }
  },

  async crearJobContrato(id: string, lang?: string): Promise<{ jobId: string; status: string }> {
    const normalizedLang = lang?.trim().toLowerCase() === 'en' ? 'en' : 'es'
    const res = await httpClient.post<BffCreateJobResponse>(
      `/api/mis-productos/${id}/documentos/contract?lang=${normalizedLang}`,
    )
    if (!res.data?.jobId) {
      throw new Error('La API no devolvió el identificador del job de consolidación.')
    }
    console.info('[Documentos] Job contractual creado', res.data)
    return res.data
  },

  async consultarJobContrato(jobId: string): Promise<DocumentConsolidationJob> {
    const res = await httpClient.get<BffJobResponse>(`/api/mis-productos/document-jobs/${jobId}`)
    if (!res.data?.jobId) {
      throw new Error('La API no devolvió el estado del job de consolidación.')
    }
    console.info('[Documentos] Estado del job contractual', res.data)
    return res.data
  },

  async descargarContrato(jobId: string, fileName = 'contratos-y-anexos-legales-formados.pdf'): Promise<string> {
    const { apiGatewayUrl } = getAppEnv()
    const base = apiGatewayUrl.endsWith('/') ? apiGatewayUrl : `${apiGatewayUrl}/`
    const token = getStoredToken()
    const response = await fetch(`${base}api/mis-productos/document-jobs/${jobId}/download`, {
      method: 'GET',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })

    if (!response.ok) {
      const err = await response.json().catch(() => null) as {
        message?: string
        error?: { message?: string }
      } | null
      throw new Error(
        err?.error?.message ?? err?.message ?? `Error al descargar el contrato (${response.status}).`,
      )
    }

    const filename = fileName
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    return filename
  },

  async descargarPdf(id: string, lang = 'es', fileName = 'proyeccion-de-la-inversion.pdf'): Promise<void> {
    const { apiGatewayUrl } = getAppEnv()
    const base = apiGatewayUrl.endsWith('/') ? apiGatewayUrl : `${apiGatewayUrl}/`

    const token = getStoredToken()
    const response = await fetch(
      `${base}api/mis-productos/${id}/pdf?lang=${lang}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    )

    if (!response.ok) {
      const err = await response.json().catch(() => null)
      throw new Error((err as { message?: string } | null)?.message ?? 'Error al generar el PDF.')
    }

    const filename = fileName

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },
}
